import styled, { keyframes } from "styled-components";
import image from "../../../../../assets/img/image3.jpg"; // swap to gov image
import { blueColor, goldColor } from "../../../../styled/Theme";
import {
  BankOutlined,
  BellOutlined,
  MessageOutlined,
  FileTextOutlined,
  CheckOutlined,
  EnvironmentOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";

const steps = [
  {
    step: "01",
    icon: <BankOutlined />,
    title: "Digital Revenue Collection",
    body: "Real-time verification of taxes, levies, and government fees across all payment channels.",
    tag: "Revenue",
    color: blueColor,
  },
  {
    step: "02",
    icon: <BellOutlined />,
    title: "Licence & Permit Alerts",
    body: "Automated notifications for renewals, compliance deadlines, and permit approvals — delivered instantly.",
    tag: "Compliance",
    color: "#0ea5e9",
  },
  {
    step: "03",
    icon: <MessageOutlined />,
    title: "Official Citizen Comms",
    body: "Secure, verified messaging between the municipality and citizens or registered businesses.",
    tag: "Communication",
    color: goldColor,
  },
  {
    step: "04",
    icon: <FileTextOutlined />,
    title: "Payment-Linked Audit Logs",
    body: "Every transaction and communication is logged, timestamped, and ready for audit or reporting.",
    tag: "Audit",
    color: "#22c55e",
  },
];

const trust = [
  { icon: <EnvironmentOutlined />, label: "Multi-municipality ready" },
  { icon: <SafetyCertificateOutlined />, label: "Regulatory compliant" },
  { icon: <CheckOutlined />, label: "Fully auditable records" },
];

export default function LocalGovernmentSolution() {
  return (
    <Wrapper>
      {/* ── Diagonal top band ── */}
      <TopBand>
        <TopBandInner className="container">
          <BandLeft
            data-aos="fade-right"
            data-aos-duration="700"
            data-aos-delay="100"
          >
            <BandEyebrow>
              <EyebrowDot />
              Local Governments &amp; Municipalities
            </BandEyebrow>
            <BandHeadline>
              Digital Public
              <br />
              <AccentWord>Service</AccentWord> Delivery
            </BandHeadline>
            <BandSubline>
              Public revenue collection and official communication — unified for
              every level of local government.
            </BandSubline>

            <TrustRow>
              {trust.map((t, i) => (
                <TrustChip
                  key={i}
                  data-aos="fade-up"
                  data-aos-delay={350 + i * 80}
                  data-aos-duration="500"
                >
                  <TrustIcon>{t.icon}</TrustIcon>
                  {t.label}
                </TrustChip>
              ))}
            </TrustRow>
          </BandLeft>

          <BandRight
            data-aos="fade-left"
            data-aos-duration="800"
            data-aos-delay="200"
          >
            <ImageWrap>
              <CivicImage src={image} alt="Local government digital services" />
              <ImageShade />
              <ImageBadge>
                <BadgePulse />
                <span>Live across 12+ municipalities</span>
              </ImageBadge>
            </ImageWrap>
          </BandRight>
        </TopBandInner>

        {/* Diagonal clip bottom */}
        <DiagonalClip />
      </TopBand>

      {/* ── Process flow ── */}
      <FlowSection className="container">
        <FlowHeader
          data-aos="fade-up"
          data-aos-duration="600"
          data-aos-delay="100"
        >
          <FlowEyebrow>How It Works</FlowEyebrow>
          <FlowTitle>
            From Collection to Communication — <GoldSpan>One Flow</GoldSpan>
          </FlowTitle>
        </FlowHeader>

        <StepsRow>
          {steps.map((s, i) => (
            <StepWrap key={i}>
              <StepCard
                $color={s.color}
                data-aos="fade-up"
                data-aos-delay={200 + i * 110}
                data-aos-duration="650"
              >
                <StepTopRow>
                  <StepNumBadge $color={s.color}>{s.step}</StepNumBadge>
                  <StepTag $color={s.color}>{s.tag}</StepTag>
                </StepTopRow>
                <StepIconWrap $color={s.color}>{s.icon}</StepIconWrap>
                <StepTitle>{s.title}</StepTitle>
                <StepBody>{s.body}</StepBody>
                <StepBottomDot $color={s.color} />
              </StepCard>

              {/* Connector arrow between cards */}
              {i < steps.length - 1 && (
                <StepConnector
                  data-aos="fade-in"
                  data-aos-delay={280 + i * 110}
                  data-aos-duration="400"
                >
                  <ConnectorLine />
                  <ConnectorArrow />
                </StepConnector>
              )}
            </StepWrap>
          ))}
        </StepsRow>
      </FlowSection>

      {/* ── Bottom full-bleed image bar ── */}
      <BottomBar>
        <BottomBarImage src={image} alt="Municipal operations" />
        <BottomBarOverlay />
        <BottomBarContent className="container">
          <BottomBarText
            data-aos="fade-up"
            data-aos-duration="600"
            data-aos-delay="100"
          >
            <BottomBarStat>40%</BottomBarStat>
            <BottomBarStatLabel>
              faster revenue reconciliation
            </BottomBarStatLabel>
          </BottomBarText>
          <BottomBarDivider />
          <BottomBarText
            data-aos="fade-up"
            data-aos-duration="600"
            data-aos-delay="200"
          >
            <BottomBarStat>100%</BottomBarStat>
            <BottomBarStatLabel>
              traceable transactions &amp; messages
            </BottomBarStatLabel>
          </BottomBarText>
          <BottomBarDivider />
          <BottomBarText
            data-aos="fade-up"
            data-aos-duration="600"
            data-aos-delay="300"
          >
            <BottomBarStat>Zero</BottomBarStat>
            <BottomBarStatLabel>unverified communications</BottomBarStatLabel>
          </BottomBarText>
        </BottomBarContent>
      </BottomBar>
    </Wrapper>
  );
}

/* ── Animations ── */
const pulseDot = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.5); }
  50% { box-shadow: 0 0 0 8px rgba(34,197,94,0); }
`;

/* ── Styles ── */

const Wrapper = styled.section`
  background: #ffffff;
  overflow: hidden;
`;

/* Top band */
const TopBand = styled.div`
  background: linear-gradient(150deg, #07142e 0%, #0d2254 55%, #0a1e48 100%);
  position: relative;
  padding-bottom: 80px;
`;

const TopBandInner = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 56px;
  align-items: center;
  padding: 64px 20px 48px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 36px;
  }
`;

const BandLeft = styled.div``;

const BandEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.1px;
  text-transform: uppercase;
  color: ${goldColor};
  background: ${goldColor}18;
  border: 1px solid ${goldColor}30;
  border-radius: 20px;
  padding: 4px 14px;
  margin-bottom: 20px;
`;

const EyebrowDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${goldColor};
  flex-shrink: 0;
`;

const BandHeadline = styled.h2`
  font-size: 42px;
  font-weight: 900;
  color: #ffffff;
  letter-spacing: -1.5px;
  line-height: 1.1;
  margin: 0 0 16px;

  @media (max-width: 768px) {
    font-size: 30px;
  }
`;

const AccentWord = styled.span`
  color: ${goldColor};
`;

const BandSubline = styled.p`
  font-size: 15px;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.7;
  margin: 0 0 28px;
  max-width: 420px;
`;

const TrustRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const TrustChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 8px;
  padding: 6px 13px;
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.22s;
  cursor: default;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
  }
`;

const TrustIcon = styled.span`
  color: ${goldColor};
  font-size: 13px;
`;

/* Image */
const BandRight = styled.div``;

const ImageWrap = styled.div`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 32px 80px rgba(0, 0, 0, 0.35);
`;

const CivicImage = styled.img`
  width: 100%;
  height: 360px;
  object-fit: cover;
  display: block;
  transition: transform 0.7s ease;

  ${ImageWrap}:hover & {
    transform: scale(1.03);
  }
`;

const ImageShade = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    transparent 50%,
    rgba(7, 20, 46, 0.6) 100%
  );
`;

const ImageBadge = styled.div`
  position: absolute;
  bottom: 16px;
  left: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.22);
  backdrop-filter: blur(12px);
  border-radius: 10px;
  padding: 8px 14px;
  font-size: 12.5px;
  font-weight: 600;
  color: #fff;
`;

const BadgePulse = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  flex-shrink: 0;
  animation: ${pulseDot} 2s ease-in-out infinite;
`;

/* Diagonal clip */
const DiagonalClip = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 80px;
  background: #ffffff;
  clip-path: polygon(0 100%, 100% 0, 100% 100%);
`;

/* Flow section */
const FlowSection = styled.div`
  padding: 64px 20px 56px;
`;

const FlowHeader = styled.div`
  margin-bottom: 44px;
`;

const FlowEyebrow = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: ${blueColor};
  margin-bottom: 10px;
`;

const FlowTitle = styled.h3`
  font-size: 26px;
  font-weight: 800;
  color: #0a1628;
  letter-spacing: -0.8px;
  margin: 0;
`;

const GoldSpan = styled.span`
  color: ${goldColor};
`;

const StepsRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0;

  @media (max-width: 860px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const StepWrap = styled.div`
  display: flex;
  align-items: center;
  flex: 1;

  @media (max-width: 860px) {
    flex-direction: column;
    width: 100%;
  }
`;

const StepCard = styled.div<{ $color: string }>`
  flex: 1;
  background: #ffffff;
  border: 1px solid #e8f0f8;
  border-top: 3px solid ${(p) => p.$color};
  border-radius: 16px;
  padding: 24px 20px 20px;
  position: relative;
  overflow: hidden;
  transition: all 0.26s ease;
  cursor: default;
  min-height: 210px;

  &:hover {
    box-shadow: 0 12px 36px ${(p) => p.$color}18;
    transform: translateY(-5px);
    border-color: ${(p) => p.$color}50;
    border-top-color: ${(p) => p.$color};
  }

  @media (max-width: 860px) {
    width: 100%;
    min-height: auto;
  }
`;

const StepTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`;

const StepNumBadge = styled.div<{ $color: string }>`
  font-size: 11px;
  font-weight: 800;
  color: ${(p) => p.$color};
  letter-spacing: 1px;
`;

const StepTag = styled.div<{ $color: string }>`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: ${(p) => p.$color};
  background: ${(p) => p.$color}14;
  border-radius: 4px;
  padding: 2px 7px;
`;

const StepIconWrap = styled.div<{ $color: string }>`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: ${(p) => p.$color}12;
  color: ${(p) => p.$color};
  font-size: 17px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  transition: background 0.25s;

  ${StepCard}:hover & {
    background: ${(p) => p.$color}22;
  }
`;

const StepTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 8px;
  line-height: 1.3;
`;

const StepBody = styled.div`
  font-size: 12.5px;
  color: #64748b;
  line-height: 1.6;
`;

const StepBottomDot = styled.div<{ $color: string }>`
  position: absolute;
  bottom: 14px;
  right: 14px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  opacity: 0.3;
`;

/* Connector */
const StepConnector = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 32px;
  flex-shrink: 0;
  padding-top: 28px;

  @media (max-width: 860px) {
    flex-direction: row;
    width: 100%;
    padding-top: 0;
    padding-left: 24px;
    height: 24px;
  }
`;

const ConnectorLine = styled.div`
  width: 100%;
  height: 1px;
  background: linear-gradient(to right, #cbd5e1, #94a3b8);
  flex: 1;

  @media (max-width: 860px) {
    width: 1px;
    height: 100%;
    background: linear-gradient(to bottom, #cbd5e1, #94a3b8);
  }
`;

const ConnectorArrow = styled.div`
  width: 0;
  height: 0;
  border-top: 4px solid transparent;
  border-bottom: 4px solid transparent;
  border-left: 6px solid #94a3b8;

  @media (max-width: 860px) {
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 6px solid #94a3b8;
    border-bottom: none;
  }
`;

/* Bottom bar */
const BottomBar = styled.div`
  position: relative;
  height: 140px;
  overflow: hidden;
`;

const BottomBarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  object-position: center 65%;
`;

const BottomBarOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    rgba(7, 20, 55, 0.88) 0%,
    rgba(7, 20, 55, 0.78) 100%
  );
`;

const BottomBarContent = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  gap: 0;
  padding: 0 20px;
`;

const BottomBarText = styled.div`
  flex: 1;
  text-align: center;
`;

const BottomBarStat = styled.div`
  font-size: 30px;
  font-weight: 900;
  color: ${goldColor};
  letter-spacing: -1px;
  line-height: 1;
`;

const BottomBarStatLabel = styled.div`
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.55);
  font-weight: 500;
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;

const BottomBarDivider = styled.div`
  width: 1px;
  height: 48px;
  background: rgba(255, 255, 255, 0.15);
`;
